import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';

import { routes } from './app.routes';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { ConfigService } from './core/services/config.service';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import {
  HttpClient,
  provideHttpClient,
  withInterceptors,
} from '@angular/common/http';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { KeyCloakService } from './core/services/keycloak.service';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { loadingInterceptor } from './core/interceptors/loading.interceptor';
import { LoadingService } from './core/services/loading.service';

import { FormlyModule } from '@ngx-formly/core';

import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import {
  maxlengthValidationMessage,
  maxValidationMessage,
  minItems,
  minlengthValidationMessage,
  minValidationMessage,
  onlyNumbers,
  requiredMessage,
  transformFieldExtension,
  uppercaseExtension,
} from './core/config/formly/formly-config';
import { FileForm } from './core/config/formly/file/file-form.component';
import { FileValueAccessor } from './directives/file-value-accessor.directive';
import { FormlyFieldStepper } from './core/config/formly/stepper/stepper-form.component';
import { FormlyFieldIban } from './core/config/formly/iban/iban-form.component';
import { FormlyMatDatepickerModule } from '@ngx-formly/material/datepicker';
import { FormlyMatSliderModule } from '@ngx-formly/material/slider';
import { FormlyMatCheckboxModule } from '@ngx-formly/material/checkbox';
import { FormlyMatFormFieldModule } from '@ngx-formly/material/form-field';
import { FormlyMatInputModule } from '@ngx-formly/material/input';
import { FormlyMatRadioModule } from '@ngx-formly/material/radio';
import { FormlyMatMultiCheckboxModule } from '@ngx-formly/material/multicheckbox';
import { FormlyMatNativeSelectModule } from '@ngx-formly/material/native-select';
import { FormlyMatSelectModule } from '@ngx-formly/material/select';
import { FormlyMatTextAreaModule } from '@ngx-formly/material/textarea';
import { OverrideTranslateLoader } from './core/config/i18n.config';
import { MatIconRegistry } from '@angular/material/icon';
import { CustomLabelType } from './core/config/formly/custom-label/custom-label.component';
import { ApiHealthService } from './services/api.health.service';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { provideNgxSkeletonLoader } from 'ngx-skeleton-loader';
import { provideMatomo, withRouter } from 'ngx-matomo-client';
import { environment } from '../environments/environment';
import { provideDeviceDetectorInitializer } from './core/services/user-agent.service';
import { FormlyRepeatTypeComponent } from './core/config/formly/repeating-section/repeating-section';
import { FormlyFieldRepeatDialog } from './core/config/formly/repeat-dialog/repeat-dialog';

export function createTranslateLoader(http: HttpClient) {
  return new OverrideTranslateLoader(http, environment.api.appVersion);
}

export const APP_MOMENT_FORMATS = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideNgxSkeletonLoader({
      theme: {
        extendsFromRoot: true,
        height: '30px',
      },
    }),
    {
      provide: Array,
      useFactory: (iconRegistry: MatIconRegistry) => {
        iconRegistry.setDefaultFontSetClass('material-symbols-outlined');
      },
      deps: [MatIconRegistry],
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'outline',
        subscriptSizing: 'fixed',
      },
    },
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
        fallbackLang: 'de',
      }),
      FormlyMatDatepickerModule,
      FormlyMatSliderModule,
      FormlyMatCheckboxModule,
      FormlyMatFormFieldModule,
      FormlyMatInputModule,
      FormlyMatRadioModule,
      FormlyMatMultiCheckboxModule,
      FormlyMatNativeSelectModule,
      FormlyMatSelectModule,
      FormlyMatTextAreaModule,
    ),
    provideAppInitializer(async () => {
      const health = inject(ApiHealthService);
      await health.getHealth();
    }),
    importProvidersFrom(
      LoggerModule.forRoot({
        level: NgxLoggerLevel.DEBUG,
      }),
    ),
    importProvidersFrom(
      FormlyModule.forRoot({
        types: [
          {
            name: 'customLabel',
            component: CustomLabelType,
          },
          {
            name: 'file',
            component: FileForm,
          },
          {
            name: 'stepper',
            component: FormlyFieldStepper,
            wrappers: [],
          },
          { name: 'iban', component: FormlyFieldIban },
          { name: 'repeating', component: FormlyRepeatTypeComponent },
          {
            name: 'repeatDialog',
            component: FormlyFieldRepeatDialog,
            wrappers: [],
          },
        ],
        validators: [
          { name: 'onlyNumbers', validation: onlyNumbers },
          { name: 'minItems', validation: minItems },
        ],
        validationMessages: [
          { name: 'required', message: requiredMessage },
          { name: 'minLength', message: minlengthValidationMessage },
          { name: 'maxLength', message: maxlengthValidationMessage },
          { name: 'min', message: minValidationMessage },
          { name: 'max', message: maxValidationMessage },
          { name: 'pattern', message: () => 'Ungültige Eingabe' },
          { name: 'onlyNumbers', message: () => 'Es darf nur Zahlen' },
        ],
        extensions: [
          {
            name: 'mat-label-transformer',
            extension: transformFieldExtension,
          },
          { name: 'uppercase', extension: uppercaseExtension },
        ],
        extras: {
          resetFieldOnHide: true,
          showError: (field) => {
            return (
              field.formControl &&
              field.formControl.invalid &&
              field.formControl.touched
            );
          },
        },
      }),
    ),
    FileValueAccessor,
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(
      withInterceptors([authInterceptor, errorInterceptor, loadingInterceptor]),
    ),
    ConfigService,
    KeyCloakService,
    LoadingService,
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    { provide: LOCALE_ID, useValue: 'de-DE' },
    { provide: MAT_DATE_LOCALE, useValue: 'de-DE' },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: APP_MOMENT_FORMATS,
    },
    provideDeviceDetectorInitializer(),
    // Conditional Tracking Analytics Injection
    ...(environment.tracking.enabled
      ? [
          provideMatomo(
            {
              trackerUrl: environment.tracking.trackingUrl,
              siteId: environment.tracking.trackingSiteId,
            },
            withRouter(),
          ),
        ]
      : []),
  ],
};
